import { useRef, useState } from 'react';
import { Search, X, Check, Plus, Loader2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { useAllTags } from '@/hooks/useTags';
import { useUpdateProductFields } from '@/hooks/useProduct';

interface TagEditorProps {
  shopifyProductId: number;
  /** The product's current tags. */
  tags: string[];
}

/**
 * Click-to-edit tag editor for a product.
 *
 * Applied tags sit at the top as removable chips. A search box filters every
 * tag already used anywhere in the catalog (shown as a checkbox list) so you
 * pick from the existing vocabulary instead of retyping -- and if what you
 * typed is new, one click creates it. Every add/remove writes the product
 * locally and pushes straight to Shopify, like the rest of the product editor.
 */
export function TagEditor({ shopifyProductId, tags }: TagEditorProps) {
  const { data: catalogTags = [], isLoading } = useAllTags();
  const update = useUpdateProductFields();

  // Optimistic copy of the applied tags. Re-synced from props (no effect
  // needed) whenever the product refetches with a different tag set.
  const [applied, setApplied] = useState(tags);
  const tagsKey = tags.join(' ');
  const lastKey = useRef(tagsKey);
  if (lastKey.current !== tagsKey) {
    lastKey.current = tagsKey;
    setApplied(tags);
  }

  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const save = (next: string[]) => {
    setApplied(next);
    update.mutate(
      { shopifyProductId, fields: { tags: next } },
      {
        onSuccess: () => toast.success('Tags saved to Shopify'),
        onError: (err) =>
          toast.error(`Saved locally, but Shopify push failed: ${err.message}`),
      },
    );
  };

  const appliedSet = new Set(applied.map((t) => t.toLowerCase()));

  const toggle = (tag: string) => {
    const lower = tag.toLowerCase();
    save(
      appliedSet.has(lower)
        ? applied.filter((t) => t.toLowerCase() !== lower)
        : [...applied, tag],
    );
  };

  const addTyped = () => {
    const value = search.trim();
    if (!value) return;
    if (!appliedSet.has(value.toLowerCase())) save([...applied, value]);
    setSearch('');
  };

  // Close the dropdown on an outside click.
  const handleBlur = (e: React.FocusEvent) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
  };

  const query = search.trim().toLowerCase();
  // Existing tags matching the search, applied ones first, then alphabetical.
  const matches = catalogTags
    .filter((t) => t.toLowerCase().includes(query))
    .sort((a, b) => {
      const aOn = appliedSet.has(a.toLowerCase());
      const bOn = appliedSet.has(b.toLowerCase());
      if (aOn !== bOn) return aOn ? -1 : 1;
      return a.localeCompare(b);
    });
  const exactExists =
    query !== '' && catalogTags.some((t) => t.toLowerCase() === query);
  const canCreate = query !== '' && !exactExists;

  return (
    <div ref={containerRef} className="relative" onBlur={handleBlur}>
      {/* Applied tags -- the ones we're using, up top */}
      {applied.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {applied.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-md border border-primary/20 bg-primary/10 py-0.5 pl-2 pr-1 text-[11px] text-primary"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                className="rounded-sm p-0.5 hover:bg-primary/20"
                onClick={() => toggle(tag)}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search / add input */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          placeholder="Search or add tags"
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTyped();
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-8 text-sm outline-none focus:border-primary sm:h-8"
        />
        {update.isPending && (
          <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown: pick from existing tags, or create the typed one */}
      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-popover py-1 shadow-md">
          {canCreate && (
            <button
              type="button"
              onClick={addTyped}
              className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-accent"
            >
              <Plus className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="truncate">
                Create <span className="font-medium">"{search.trim()}"</span>
              </span>
            </button>
          )}

          {isLoading ? (
            <p className="px-2.5 py-2 text-xs text-muted-foreground">Loading tags...</p>
          ) : matches.length === 0 && !canCreate ? (
            <p className="px-2.5 py-2 text-xs text-muted-foreground">
              {catalogTags.length === 0
                ? 'No tags yet - type to add one.'
                : 'No matching tags.'}
            </p>
          ) : (
            matches.map((tag) => {
              const on = appliedSet.has(tag.toLowerCase());
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggle(tag)}
                  className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-accent"
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      on
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border',
                    )}
                  >
                    {on && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{tag}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
